import React, { ChangeEvent, FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { isNil, orderBy } from 'lodash';
import { makeStyles, Theme } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import GET_STUDENT_SETTINGS from 'graph/student/get-student-settings-query';
import GET_KEYBOARDING_CLASSROOM_SETTINGS from 'graph/settings/key/get-keyboarding-classroom-settings-query';
import UPDATE_KEY_CLASSROOM_SKIP_JUMP_IN_SETTING from 'graph/settings/key/update-key-classroom-skip-jump-in-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import { StudentSetting, BaseSettingData, Classroom } from 'types/schema-generated-types';
import { getActiveStudentProducts } from 'utilities/product-utils';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface JumpIntoKeyboardingProps {
  classroom: Classroom;
  setting: BaseSettingData | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    },
    listContainer: {
      paddingLeft: theme.spacing(2)
    },
    listItem: {
      marginLeft: theme.spacing(2)
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: theme.typography.pxToRem(24),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    }
  };
});

const getClassroomKeySettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateKeyClassroomSettingSkipJumpIn }
      ): { keyClassroomSettings: any } => {
        const [{ value: skipJumpIn }] = updateKeyClassroomSettingSkipJumpIn;
        return {
          keyClassroomSettings: {
            ...cachedData?.keyClassroomSettings,
            skipJumpIn
          }
        };
      }
    }
  ])
});

const JumpIntoKeyboarding: FC<JumpIntoKeyboardingProps> = ({
  classroom,
  expanded,
  setting,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('JumpIntoKeyboardingSetting');
  const classes = useStyles();
  const [, { openAlert }] = useStore();
  const [updateKeyClassroomSettingSkipJumpIn] = useMutation(
    UPDATE_KEY_CLASSROOM_SKIP_JUMP_IN_SETTING,
    getClassroomKeySettingsMutationCacheUpdate(classroom.id)
  );

  const students = classroom?.students || [];
  const { data } = useQuery(GET_STUDENT_SETTINGS, {
    variables: {
      where: {
        studentId: students.map(student => student.id),
        setting: ['skipJumpIn']
      }
    },
    fetchPolicy: 'network-only'
  });

  const isOverridden = !isNil(setting?.enabled);
  const isEnabled = setting?.enabled || false;

  const [overrideDefault, setOverrideDefault] = useState(isOverridden);
  const [enableSkipJump, setEnableSkipJump] = useState(isEnabled);

  const studentSettings: StudentSetting[] = data?.studentSettings ?? [];
  const handleToggle = (): void => setOverrideDefault(!overrideDefault);
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEnableSkipJump((event.target as HTMLInputElement).value === 'true');
  };
  const handleOnCancel = (): void => {
    setOverrideDefault(isOverridden);
    setEnableSkipJump(isEnabled);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateKeyClassroomSettingSkipJumpIn({
        variables: {
          data: {
            classroomId: classroom.id,
            value: { ...(overrideDefault && { enabled: enableSkipJump }) }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Skip First 6 Weeks', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  const overriddenStudents = useMemo(() => {
    const overriddenStudents = students.filter(student => {
      const setting = studentSettings.find(
        (setting: StudentSetting) => setting.studentId === student.id
      );

      if (isNil(setting)) return false;
      if (!student.licenseUsage) return false;

      const productCode = getActiveStudentProducts(student.licenseUsage).find(
        ({ product }) => product.family.code === 'KEY'
      )?.product?.code;

      return productCode && !isNil(setting.value?.[productCode]);
    });

    return orderBy(
      overriddenStudents,
      [student => student.lastName.toLowerCase(), student => student.firstName.toLowerCase()],
      'asc'
    );
  }, [students, studentSettings]);

  return (
    <SettingsCard
      title={t('Skip First 6 Weeks')}
      panel="jumpIntoKeyboarding"
      description={t(
        'Enable or disable this setting to skip first six weeks of foundational skills and start at week 7 for all students in the class.'
      )}
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <Fragment>
        <FormControl component="fieldset" className={classes.container}>
          <FormLabel component="label" className={classes.label}>
            {t(
              'By default, students in grades K-2 start at week 1 and students in grades 3-5 start at week 7. To change this behavior, select "Class Level Setting" below.'
            )}
          </FormLabel>
          <FormControlLabel
            control={
              <Switch
                checked={overrideDefault}
                onChange={handleToggle}
                name="JumpIntoKeyboarding"
                color="primary"
              />
            }
            label={t('Class Level Setting')}
          />
        </FormControl>
        {overrideDefault && (
          <div>
            <FormControl component="fieldset" className={classes.container}>
              <RadioGroup
                aria-label={t('Change skip first 6 weeks class setting')}
                name="JumpIntoKeyboarding"
                value={enableSkipJump}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="false"
                  checked={!enableSkipJump}
                  control={<Radio />}
                  label={t('Start class at week 1')}
                />
                <FormControlLabel
                  value="true"
                  checked={enableSkipJump}
                  control={<Radio />}
                  label={t('Skip first 6 weeks and start class at week 7')}
                />
              </RadioGroup>
            </FormControl>
            <Typography component="div" className={classes.container}>
              <Typography variant="body1" component="p">
                {t(
                  'The following students have custom Skip First 6 Weeks settings that will take precendence over this class setting:'
                )}
              </Typography>
              <ul className={classes.listContainer}>
                {overriddenStudents.map(student => (
                  <Typography
                    key={`affected-student-name-${student.id}`}
                    variant="body1"
                    component="li"
                    className={classes.listItem}
                  >
                    {`${student.firstName} ${student.lastName} (${student.username})`}
                  </Typography>
                ))}
              </ul>
            </Typography>
          </div>
        )}
      </Fragment>
    </SettingsCard>
  );
};

export default JumpIntoKeyboarding;
